/**
 * User-profile realtor agent fields stored under `metadata.realtor`.
 */

export const BROKERAGE_LICENSE_NUMBER_MIN_LENGTH = 7;
export const BROKERAGE_LICENSE_NUMBER_MAX_LENGTH = 15;

/** Letters, numbers, and hyphens only. */
export const BROKERAGE_LICENSE_NUMBER_PATTERN = /^[A-Z0-9-]{7,15}$/;

/** UX: bounded realtor identity fields with modest headroom beyond typical values. */
export const REALTOR_AGENT_FIELD_MAX_LENGTH = {
  display_name: 150,
  brokerage_name: 250,
  brokerage_license_number: BROKERAGE_LICENSE_NUMBER_MAX_LENGTH,
};

/**
 * @typedef {{ display_name: string, brokerage_name: string, brokerage_license_number: string }} RealtorAgent
 */

/**
 * @returns {RealtorAgent}
 */
export function createEmptyRealtorAgent() {
  return {
    display_name: "",
    brokerage_name: "",
    brokerage_license_number: "",
  };
}

/** Stable empty default for prop fallbacks (avoids new object each render). */
export const EMPTY_REALTOR_AGENT = createEmptyRealtorAgent();

/**
 * @param {unknown} raw
 * @returns {object}
 */
export function parseUserMetadata(raw) {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed != null && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
  return {};
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function sanitizeBrokerageLicenseNumberInput(value) {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "")
    .slice(0, BROKERAGE_LICENSE_NUMBER_MAX_LENGTH);
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidBrokerageLicenseNumber(value) {
  const normalized = sanitizeBrokerageLicenseNumberInput(String(value ?? "").trim());
  return BROKERAGE_LICENSE_NUMBER_PATTERN.test(normalized);
}

/**
 * @param {unknown} input
 * @returns {RealtorAgent}
 */
export function normalizeRealtorAgent(input) {
  const source = input && typeof input === "object" ? input : {};
  return {
    display_name: String(source.display_name ?? "").trim(),
    brokerage_name: String(source.brokerage_name ?? "").trim(),
    brokerage_license_number: sanitizeBrokerageLicenseNumberInput(source.brokerage_license_number),
  };
}

/**
 * @param {unknown} metadata
 * @returns {RealtorAgent}
 */
export function readRealtorAgentFromMetadata(metadata) {
  const parsed = parseUserMetadata(metadata);
  return normalizeRealtorAgent(parsed.realtor);
}

/**
 * Realtor snapshot persisted on a distributed calculation payload.
 *
 * @param {unknown} calculation
 * @returns {RealtorAgent}
 */
export function readRealtorAgentFromCalculation(calculation) {
  let parsed = calculation;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return createEmptyRealtorAgent();
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return createEmptyRealtorAgent();
  }
  return normalizeRealtorAgent(parsed.realtor);
}

/**
 * @param {...unknown} candidates
 * @returns {RealtorAgent}
 */
export function pickCompleteRealtorAgent(...candidates) {
  for (const candidate of candidates) {
    const normalized = normalizeRealtorAgent(candidate);
    if (isRealtorAgentComplete(normalized)) return normalized;
  }
  return createEmptyRealtorAgent();
}

/**
 * @param {unknown} existing
 * @param {unknown} realtorAgent
 * @returns {object}
 */
export function mergeRealtorAgentIntoMetadata(existing, realtorAgent) {
  const base = parseUserMetadata(existing);
  return {
    ...base,
    realtor: normalizeRealtorAgent(realtorAgent),
  };
}

/**
 * @param {unknown} realtor
 * @returns {boolean}
 */
export function isRealtorAgentComplete(realtor) {
  const normalized = normalizeRealtorAgent(realtor);
  return Boolean(
    normalized.display_name &&
      normalized.brokerage_name &&
      isValidBrokerageLicenseNumber(normalized.brokerage_license_number)
  );
}

/**
 * Canonical updateUser `user_id` from a getUser profile body.
 * Dynamo user records use HMAC `id` (see cai-services User model); update_user
 * loads by that id via User.get(id=...). Do not send Cognito username/email.
 *
 * @param {unknown} userProfile
 * @returns {string}
 */
export function resolveUserIdForProfileUpdate(userProfile) {
  const profile = userProfile && typeof userProfile === "object" ? userProfile : {};
  return String(profile.id ?? profile.user_id ?? "").trim();
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeLookupEmail(value) {
  const email = String(value ?? "")
    .trim()
    .toLowerCase();
  return email.includes("@") ? email : "";
}

/**
 * Email for a live getUser of the distributing realtor.
 * Prefer sender when that party is a realtor and the id is email-like;
 * otherwise homeowner_email (blast copies the realtor email there).
 *
 * @param {unknown} deal
 * @returns {string}
 */
export function resolveRealtorLookupEmail(deal) {
  const row = deal && typeof deal === "object" ? deal : {};
  const senderRole = String(row.sender_role ?? "")
    .trim()
    .toLowerCase();
  const senderEmail = normalizeLookupEmail(row.sender);
  if (senderRole === "realtor" && senderEmail) return senderEmail;
  return normalizeLookupEmail(row.homeowner_email);
}
